import { useEffect, useState } from 'react'
import { apiClient } from '@/services/apiClient'

interface PromptCaptureSettingsResponse {
  enabled: boolean
  source: 'admin' | 'env-default'
  env_default: boolean
  retention_days: number
}

export function PromptCaptureSettings() {
  const [settings, setSettings] = useState<PromptCaptureSettingsResponse>({
    enabled: false,
    source: 'env-default',
    env_default: false,
    retention_days: 30,
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string>('')

  useEffect(() => {
    apiClient
      .get<PromptCaptureSettingsResponse>('/api/admin/prompt-capture')
      .then((s) => setSettings(s))
      .catch((e) => {
        console.error('Failed to load prompt capture settings', e)
      })
  }, [])

  const save = async () => {
    setSaving(true)
    setError('')
    try {
      const updated = await apiClient.put<PromptCaptureSettingsResponse>(
        '/api/admin/prompt-capture',
        { enabled: settings.enabled },
      )
      setSettings(updated)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unknown error')
    } finally {
      setSaving(false)
    }
  }

  const deploymentDefault = settings.env_default ? 'on' : 'off'
  const isOverridden = settings.source === 'admin'

  return (
    <div className="space-y-4 max-w-lg">
      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={settings.enabled}
          onChange={(e) => setSettings((s) => ({ ...s, enabled: e.target.checked }))}
        />
        Capture composed prompts on rendered turns
      </label>
      <p className="text-xs text-muted-foreground">
        Snapshots contain raw customer content; reads require the prompt-snapshots:read permission.
      </p>

      <div className="border-t pt-4 space-y-2">
        <p className="text-sm">
          Deployment default: <strong>{deploymentDefault}</strong>
          {isOverridden && ' (currently overridden by admin setting)'}
        </p>
        <p className="text-sm">
          Snapshots kept <strong>{settings.retention_days}</strong> days
        </p>
      </div>

      {error && (
        <div className="text-sm text-destructive">
          {error}
        </div>
      )}

      <button onClick={save} disabled={saving}>
        Save
      </button>
    </div>
  )
}
